import type { RowSelectionState } from "@tanstack/react-table";
import type { ReportSettingsTemplateTargetsResponse } from "@common/schemas/domains/reportSettings/report-settings-template-application.schema";
import type { WorkspaceSchema } from "@common/schemas/workspace.schema";
import { COA_TEMPLATE_MANAGE_FORBIDDEN_TOOLTIP } from "@components/settings/ChartOfAccountsTemplates/constants";
import { resolveInheritedReportTemplate } from "@common/helpers/report-settings/resolve-inherited-report-template";
import {
    getSavedTemplateKey,
    indexTargetFacts,
    isOwnedByAnotherFirm,
    type TargetFacts,
} from "./targetFacts";

type WorkspaceEntity = WorkspaceSchema["entities"][number];

export type AppliedTemplateSource = "firm" | "client" | "entity";

export const APPLIED_TEMPLATE_SOURCE_LABELS: Record<
    AppliedTemplateSource,
    string
> = {
    firm: "Firm default",
    client: "Client default",
    entity: "Entity default",
};

export interface AppliedTemplateLabel {
    name: string;
    source: AppliedTemplateSource;
}

export interface ApplyReportTemplateRow {
    id: string;
    workspaceId: string;
    name: string;
    isClientRow: boolean;
    iconColor: string;
    logo: string | null;
    isSelectable: boolean;
    canOpenSettings: boolean;
    disabledTooltip?: string;
    overrideCount: number | null;
    currentTemplate: AppliedTemplateLabel | null;
    newTemplate: AppliedTemplateLabel | null;
    savedTemplateKey: string | null;
    children?: ApplyReportTemplateRow[];
}

export interface ApplyTreeWorkspace extends Pick<
    WorkspaceSchema,
    "id" | "name"
> {
    entities: Array<
        Pick<
            WorkspaceEntity,
            "id" | "name" | "iconColor" | "logo" | "isPersonal" | "isMockEntity"
        >
    >;
}

interface AppliedTemplate {
    templateId: string | null;
    name: string;
}

const WORKSPACE_ROW_PREFIX = "ws-";

export const workspaceRowId = (workspaceId: string) =>
    `${WORKSPACE_ROW_PREFIX}${workspaceId}`;
export const entityRowId = (entityId: number) => String(entityId);

export const parseRowId = (
    rowId: string,
):
    | { kind: "workspace"; workspaceId: string }
    | { kind: "entity"; entityId: number } =>
    rowId.startsWith(WORKSPACE_ROW_PREFIX)
        ? {
              kind: "workspace",
              workspaceId: rowId.slice(WORKSPACE_ROW_PREFIX.length),
          }
        : { kind: "entity", entityId: Number(rowId) };

const isAppliedDirectly = ({
    facts,
    template,
}: {
    facts: TargetFacts;
    template: AppliedTemplate;
}) => facts.appliedTemplate?.templateId === template.templateId;

export const getSavedSelection = ({
    targets,
    template,
}: {
    targets: ReportSettingsTemplateTargetsResponse;
    template: AppliedTemplate;
}): RowSelectionState => {
    const selection: RowSelectionState = {};
    for (const facts of targets.workspaces) {
        if (!isAppliedDirectly({ facts, template })) {
            continue;
        }
        selection[workspaceRowId(facts.workspaceId)] = true;
    }
    for (const facts of targets.entities) {
        if (!isAppliedDirectly({ facts, template })) {
            continue;
        }
        selection[entityRowId(facts.entityId)] = true;
    }
    return selection;
};

const getUncheckedDirectTemplateName = ({
    facts,
    template,
}: {
    facts: TargetFacts;
    template: AppliedTemplate;
}) =>
    isAppliedDirectly({ facts, template })
        ? null
        : (facts.appliedTemplate?.templateName ?? null);

const ARCHIVED_TEMPLATE_TOOLTIP = "Enable this template to apply it";

const getBlockReasonTooltip = ({
    facts,
    owner,
}: {
    facts: TargetFacts;
    owner: "client" | "entity";
}) => {
    if (facts.blockReason === "ownedByAnotherFirm") {
        return "Another firm manages report templates for this client";
    }
    if (facts.blockReason === "cannotEditSettings") {
        return `You don't have permission to edit this ${owner}'s settings`;
    }
    return undefined;
};

export const buildApplyReportTemplateRows = ({
    workspaces,
    targets,
    template,
    defaultTemplateName,
    defaultTemplateKey,
    selection,
    canManage,
    isTemplateArchived,
}: {
    workspaces: ApplyTreeWorkspace[];
    targets: ReportSettingsTemplateTargetsResponse;
    template: AppliedTemplate;
    defaultTemplateName: string;
    defaultTemplateKey: string;
    selection: RowSelectionState;
    canManage: boolean;
    isTemplateArchived: boolean;
}): ApplyReportTemplateRow[] => {
    const savedSelection = getSavedSelection({ targets, template });
    const { workspaceFactsById, entityFactsById } = indexTargetFacts(targets);

    const getDirectName = ({
        facts,
        id,
        checks,
    }: {
        facts: TargetFacts | undefined;
        id: string;
        checks: RowSelectionState;
    }) => {
        if (!facts) {
            return null;
        }
        return checks[id]
            ? template.name
            : getUncheckedDirectTemplateName({ facts, template });
    };

    const getTemplateLabel = ({
        workspace,
        entity,
        checks,
    }: {
        workspace: ApplyTreeWorkspace;
        entity?: ApplyTreeWorkspace["entities"][number];
        checks: RowSelectionState;
    }): AppliedTemplateLabel => {
        const clientDirect = getDirectName({
            facts: workspaceFactsById.get(workspace.id),
            id: workspaceRowId(workspace.id),
            checks,
        });
        const entityDirect = entity
            ? getDirectName({
                  facts: entityFactsById.get(entity.id),
                  id: entityRowId(entity.id),
                  checks,
              })
            : null;
        const { value, level } = resolveInheritedReportTemplate({
            entity: entityDirect,
            workspace: clientDirect,
            firmDefault: defaultTemplateName,
        });
        if (level === "entity") {
            return { name: value, source: "entity" };
        }
        if (level === "workspace") {
            return { name: value, source: "client" };
        }
        return { name: value, source: "firm" };
    };

    const isSameLabel = (
        left: AppliedTemplateLabel | null,
        right: AppliedTemplateLabel | null,
    ) => left?.name === right?.name && left?.source === right?.source;

    const getRowFields = ({
        facts,
        workspace,
        entity,
    }: {
        facts: TargetFacts | undefined;
        workspace: ApplyTreeWorkspace;
        entity?: ApplyTreeWorkspace["entities"][number];
    }) => {
        if (!facts) {
            return {
                isSelectable: false,
                canOpenSettings: false,
                overrideCount: null,
                currentTemplate: null,
                newTemplate: null,
                savedTemplateKey: null,
            };
        }
        const isForeignRow = isOwnedByAnotherFirm(facts);
        const rowId = entity
            ? entityRowId(entity.id)
            : workspaceRowId(workspace.id);
        const isLockedByArchive = isTemplateArchived && !savedSelection[rowId];
        const templateLabel = isForeignRow
            ? null
            : getTemplateLabel({ workspace, entity, checks: selection });
        const savedTemplate = isForeignRow
            ? null
            : getTemplateLabel({ workspace, entity, checks: savedSelection });
        const blockReasonTooltip = getBlockReasonTooltip({
            facts,
            owner: entity ? "entity" : "client",
        });
        const archivedTooltip = isLockedByArchive
            ? ARCHIVED_TEMPLATE_TOOLTIP
            : undefined;
        return {
            isSelectable:
                canManage && facts.blockReason === null && !isLockedByArchive,
            canOpenSettings: canManage && facts.blockReason === null,
            disabledTooltip: canManage
                ? (blockReasonTooltip ?? archivedTooltip)
                : COA_TEMPLATE_MANAGE_FORBIDDEN_TOOLTIP,
            overrideCount: facts.overrideCount,
            currentTemplate: savedTemplate,
            newTemplate: isSameLabel(savedTemplate, templateLabel)
                ? null
                : templateLabel,
            savedTemplateKey: getSavedTemplateKey({
                facts,
                clientFacts: workspaceFactsById.get(workspace.id),
                defaultTemplateKey,
            }),
        };
    };

    return workspaces.map((workspace) => {
        const children = workspace.entities
            .filter((entity) => !entity.isPersonal && !entity.isMockEntity)
            .map((entity): ApplyReportTemplateRow => ({
                id: entityRowId(entity.id),
                workspaceId: workspace.id,
                name: entity.name ?? entityRowId(entity.id),
                isClientRow: false,
                iconColor: entity.iconColor,
                logo: entity.logo,
                ...getRowFields({
                    facts: entityFactsById.get(entity.id),
                    workspace,
                    entity,
                }),
            }));

        return {
            id: workspaceRowId(workspace.id),
            workspaceId: workspace.id,
            name: workspace.name,
            isClientRow: true,
            iconColor: "",
            logo: null,
            ...getRowFields({
                facts: workspaceFactsById.get(workspace.id),
                workspace,
            }),
            children,
        };
    });
};
